import { useCallback, useRef, useState } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import Loading from "src/components/Loading";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import SearchForm from "./SearchForm";
import ResultsTable from "./ResultsTable";
import type {
  ILitigatorCase,
  ILitigatorCaseDetails,
  ILitigatorSearchResponse,
  ILitigatorCredits,
  SearchCategory,
} from "src/interfaces/ComplianceGuide/ILitigatorSearch";

const PAGE_SIZE = 10;

const LitigatorSearch = () => {
  const { loginId, acctId, csrfToken } = useAppSelector(selectAuth);

  const [cases, setCases] = useState<ILitigatorCase[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchCategories, setSearchCategories] = useState<SearchCategory[]>(
    [],
  );
  const [warningMessage, setWarningMessage] = useState("");
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  // Case details fetched on demand, keyed by encrypted caseId. Search results already
  // carry everything the detail panel showed before DNC-339; the case summary is the only
  // reason for the extra round trip, so it is loaded per row on expand rather than for
  // all ten rows of every search.
  const [caseDetails, setCaseDetails] = useState<
    Record<string, ILitigatorCaseDetails>
  >({});
  const [loadingDetailIds, setLoadingDetailIds] = useState<Set<string>>(
    new Set(),
  );

  // Execute the search API call
  const executeSearch = async (
    text: string,
    categories: SearchCategory[],
    newSkip: number,
  ) => {
    setIsLoading(true);
    setWarningMessage("");

    try {
      const pageNum = Math.floor(newSkip / PAGE_SIZE) + 1;
      const requestBody = {
        searchText: text,
        searchCategories: categories,
        page: pageNum,
        pageSize: PAGE_SIZE,
      };

      const response: ILitigatorSearchResponse = await callApi(
        "LitigatorSearch/Search",
        HttpMethod.POST,
        requestBody,
      );

      setCases(response.cases);
      setTotalCount(response.totalCount);
      setSkip(newSkip);

      // Set appropriate warning messages
      if (response.totalCount === 0 && response.isLitigatorList) {
        setWarningMessage(
          "Litigator is from a demand letter or crowd sourced.",
        );
      } else if (response.totalCount === 0 && !response.isLitigatorList) {
        setWarningMessage("Sorry, there were no search results found.");
      } else {
        setWarningMessage("");
      }

      // Log the search action (fire-and-forget)
      callApi("LitigatorSearch/LogAction", HttpMethod.POST, {
        actionTypeId: 1,
        searchText: text,
        searchCategory: categories.join(","),
      }).catch(() => {
        // Logging failure is non-critical; silently ignore
      });
    } catch (error: unknown) {
      // Handle credit limit exceeded (BadRequest)
      const err = error as { status?: number; data?: { message?: string } };
      if (err?.status === 400) {
        setWarningMessage(
          err?.data?.message ||
            "You have exceeded your search credit limit. Please contact support.",
        );
      } else {
        setWarningMessage("An error occurred while searching. Please try again.");
      }
      setCases([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Called by SearchForm on submit
  const handleSearch = (text: string, categories: SearchCategory[]) => {
    setSearchText(text);
    setSearchCategories(categories);
    setSkip(0);
    executeSearch(text, categories, 0);
  };

  // Called by Kendo Grid page change
  const handlePageChange = (newSkip: number) => {
    setSkip(newSkip);
    executeSearch(searchText, searchCategories, newSkip);
  };

  // Case ids already requested. Held in a ref rather than derived from the two state values
  // above for two reasons: the check has to be synchronous, so two fast clicks cannot both
  // start a request, and keeping it out of the render cycle lets this callback stay
  // referentially stable — ResultsTable builds its Kendo cell components from it, and those
  // remount (dropping keyboard focus mid-navigation) whenever its identity changes.
  const requestedDetailIds = useRef<Set<string>>(new Set());

  // Called by ResultsTable when a row opens. Idempotent, so the caller does not have to
  // track which cases it has already asked for.
  //
  // A successful result is cached for the life of the page: the encrypted caseId is stable,
  // the case behind it does not change while the user is looking at it, and re-fetching
  // would log a duplicate ViewDetailsPage event on every collapse and re-expand.
  const handleRequestCaseDetails = useCallback(async (caseId: string) => {
    if (requestedDetailIds.current.has(caseId)) return;
    requestedDetailIds.current.add(caseId);

    setLoadingDetailIds((prev) => new Set(prev).add(caseId));
    try {
      // caseId is interpolated as-is, not through encodeURIComponent: the backend already
      // URL-encodes the value it hands out, and its decrypt path expects one round of
      // percent-decoding to happen in transit. DownloadPdf passes it the same way.
      const details: ILitigatorCaseDetails = await callApi(
        `LitigatorSearch/CaseDetails?caseId=${caseId}`,
        HttpMethod.GET,
      );
      setCaseDetails((prev) => ({ ...prev, [caseId]: details }));
    } catch (error) {
      // Released from the requested set so the next open retries — a failed load is usually
      // transient, and meanwhile the panel renders from the search result exactly as a case
      // with no summary does. No warning banner: the user asked to see a case, not to fetch
      // a summary, so this must not read as the case itself having failed to load.
      requestedDetailIds.current.delete(caseId);
      console.error("CaseDetails error:", error);
    } finally {
      setLoadingDetailIds((prev) => {
        const next = new Set(prev);
        next.delete(caseId);
        return next;
      });
    }
  }, []);

  // Called by CaseDetails PDF download button
  const handlePdfDownload = async (caseId: string) => {
    setDownloadingIds((prev) => new Set(prev).add(caseId));
    try {
      const credits: ILitigatorCredits = await callApi(
        "LitigatorSearch/Credits",
        HttpMethod.GET,
      );

      if (!credits.hasDownloadCredits) {
        setWarningMessage(
          "You have exceeded your PDF download credit limit. Please contact support.",
        );
        return;
      }

      // Raw fetch is used here instead of callApi because the endpoint returns
      // a binary PDF blob, not JSON. callApi/callApiWithOptions parse responses
      // as JSON which would corrupt the binary data.
      const baseUrl = import.meta.env.VITE_APP_API_URL;
      const headers = new Headers();
      headers.append("loginId", loginId ?? "");
      headers.append("acctId", acctId ?? "");
      headers.append("csrfToken", csrfToken ?? "");

      const response = await fetch(
        `${baseUrl}/LitigatorSearch/DownloadPdf?caseId=${caseId}`,
        { method: "GET", headers, credentials: "include" },
      );

      if (!response.ok) {
        const text = await response.text();
        let message = "An error occurred while downloading the PDF.";
        try {
          const errorBody = JSON.parse(text);
          message = errorBody.message || message;
        } catch {
          if (text) message = text;
        }
        console.error("DownloadPdf error:", response.status, message);
        setWarningMessage(message);
        return;
      }

      // Extract filename from Content-Disposition header (server sends GUID.pdf)
      const disposition = response.headers.get("Content-Disposition");
      let fileName = (typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`) + ".pdf";
      if (disposition) {
        const match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]*)["']?/);
        if (match?.[1]) fileName = match[1];
      }

      // Create a blob URL and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("DownloadPdf exception:", err);
      setWarningMessage("An error occurred while downloading the PDF.");
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(caseId);
        return next;
      });
    }
  };

  return (
    <div>
      <SearchForm onSearch={handleSearch} isLoading={isLoading} />

      {warningMessage && (
        <div className="alert alert-warning mt-3" role="alert">
          {warningMessage}
        </div>
      )}

      {isLoading && <Loading />}

      {!isLoading && cases.length > 0 && (
        <>
          <div className="mt-3 mb-2 text-muted">
            Total results: {totalCount}
          </div>
          <ResultsTable
            cases={cases}
            onDownloadPdf={handlePdfDownload}
            downloadingIds={downloadingIds}
            onRequestCaseDetails={handleRequestCaseDetails}
            caseDetails={caseDetails}
            loadingDetailIds={loadingDetailIds}
            skip={skip}
            take={PAGE_SIZE}
            total={totalCount}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

export default LitigatorSearch;
